import React, { useEffect, useRef } from 'react'
import { SvgCross, SvgDrag } from '../assets/images'
import {
  getFocusableElements,
  restorePreviousFocus,
} from '../utils/tab-indexing'
import Draggable from 'react-draggable'
import { v4 as uuid } from 'uuid'

interface DialogProps {
  isOpen: boolean
  onClose: () => void
  showCloseButton?: boolean
  children: React.ReactNode
  width?: string
  isDragable?: boolean
}

const CustomDialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  showCloseButton,
  children,
  width = 'w-fit max-w-[98%]',
  isDragable = true,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null)
  const handleClassRef = useRef(`drag-handle-${uuid()}`)
  const handleClass = handleClassRef.current

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog) {
      if (isOpen) {
        previouslyFocusedElementRef.current =
          document.activeElement as HTMLElement
        dialog.setAttribute('closedby', 'any')
        dialog.showModal()
        setTimeout(() => {
          const firstFocusable = getFocusableElements(panelRef.current)?.[0]
          firstFocusable?.focus()
        }, 0)
      } else {
        dialog.close()
        restorePreviousFocus(previouslyFocusedElementRef.current)
      }
    }
    return () => {
      restorePreviousFocus(previouslyFocusedElementRef.current)
    }
  }, [isOpen])

  const isBackdropInteractive = () => {
    const dialog = dialogRef.current
    if (!dialog) return true
    return getComputedStyle(dialog, '::backdrop').pointerEvents !== 'none'
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (e.key === 'Tab') {
      const focusableElements = getFocusableElements(panelRef.current)
      if (focusableElements.length === 0) return

      const first = focusableElements[0]
      const last = focusableElements[focusableElements.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      onClose?.()
    }
  }

  const handleCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    // Agent backdrop is non-interactive — block light-dismiss, keep Escape via keydown.
    if (!isBackdropInteractive()) {
      e.preventDefault()
      return
    }
    onClose?.()
  }

  return (
    <dialog
      ref={dialogRef}
      onKeyDown={handleKeyDown}
      onCancel={handleCancel}
      className="m-0 border-0 bg-transparent p-0 outline-none overflow-visible"
      style={{
        width: 0,
        height: 0,
        maxWidth: 'none',
        maxHeight: 'none',
      }}
    >
      {/*
        Full-viewport drag layer only (like Modal). Dialog box stays 0×0 so
        outside clicks hit the real ::backdrop — agent CSS can block dismiss.
      */}
      <div className="fixed inset-0 z-[40] flex items-center justify-center overflow-hidden pointer-events-none">
        <Draggable
          key={isOpen ? 'dialog-open' : 'dialog-closed'}
          nodeRef={panelRef}
          handle={`.${handleClass}`}
          cancel="input, textarea, select"
          bounds="parent"
          disabled={!isDragable}
        >
          <div
            ref={panelRef}
            className={`pointer-events-auto relative bg-white rounded-[1rem] shadow-lg overflow-y-auto p-3 ${width}`}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          >
            {isDragable && (
              <div className="absolute top-3 left-4 right-4 translate-y-[-50%] z-10">
                <button
                  type="button"
                  className={`${handleClass} cursor-move w-full flex justify-center`}
                >
                  <SvgDrag className="rotate-90" />
                </button>
              </div>
            )}
            {showCloseButton ? (
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={onClose}
                  className="absolute top-0 right-0 p-2 text-accent-500 hover:text-accent-700"
                >
                  <SvgCross />
                </button>
              </div>
            ) : null}
            <div>{children}</div>
          </div>
        </Draggable>
      </div>
    </dialog>
  )
}

export default CustomDialog
